import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TouchableWithoutFeedback,
  View,
  Animated,
  Easing,
} from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { colors, toRGB } from '@styleseat/brand';
import { BodyText, Button } from '../../../ui';
import FeatureFlags from '../../../../modules/FeatureFlags';

import GlyphIcon, { BLOCKED, INFO } from '../../../ui/GlyphIcon';

import AvatarIcon from '../../../AvatarIcon';

import analytics from '../../../../modules/analytics';
import formatPhoneNumber from '../../../../modules/formatters/formatPhoneNumber';
import { openExternalUrl } from '../../../../modules/openExternalUrl';
import { getSharingLinks } from '../../../consumer/sharing/social/helpers';
import { generateSmsUrl } from '../../../consumer/sharing/social/sms';
import { getCellPhone, getPhoneType } from '../../../../modules/provider/ProClientList';

import useMount from '../../../../hooks/useMount';

import './ContactListItem.module.scss';
import { LAST_INVITED_TO_BOOK, updateSelfPromotionDates } from '../../../../modules/Api';
import { ShareTarget } from '../../../../modules/share';
import { matchWithSavedPhoneNumber } from '../../../../modules/provider/ClientFields';

export default function ContactListItem({
  contact,
  disabled,
  disabledContactInfo,
  isRecent,
  isSearchingByPhoneNumber,
  provider,
  searchTerm,
  selectContact,
  selected,
  showInviteActions,
  hasBeenInvitedBefore,
  onInviteClick,
  trackingParams,
  shouldAnimate,
  animationDelay,
}) {
  const [animation, setAnimation] = useState();
  const white = toRGB(colors.whiteWash);
  const blue = toRGB(colors.chill);

  useMount(() => {
    if (shouldAnimate) {
      setAnimation(new Animated.Value(0));
    }
  });

  useEffect(() => {
    if (animation) {
      Animated.timing(animation, {
        toValue: 1,
        easing: Easing.out(Easing.cubic),
        duration: 500,
        delay: animationDelay,
      }).start(() => {
        Animated.timing(animation, {
          toValue: 0,
          easing: Easing.in(Easing.cubic),
          duration: 500,
          delay: 2000,
        }).start();
      });
    }
  }, [
    animation,
    animationDelay,
  ]);

  const colorInterpolation = animation ? animation.interpolate({
    inputRange: [
      0,
      1,
    ],
    outputRange: [
      white,
      blue,
    ],
  }) : colors.whiteWash;

  const handleContactClick = () => {
    analytics.track('pro_contact_details_contact_clicked', trackingParams);
    selectContact(contact);
  };

  const handleInfoClick = () => {
    analytics.track('pro_contact_details_info_clicked', trackingParams);
    disabledContactInfo?.();
  };

  const handleActionClick = async () => {
    const phone = getCellPhone(contact.phones);
    if (phone) {
      const useAlternativeCopy = (
        !hasBeenInvitedBefore
        && await FeatureFlags.isEnabled('client_invite_to_ss_alt_copy_FLY-2632')
      );

      analytics.track('pro_contact_details_invite_to_book_clicked', {
        alternativeCopy: useAlternativeCopy,
        ...trackingParams,
      });
      updateSelfPromotionDates(provider.id, [LAST_INVITED_TO_BOOK]);
      const links = await getSharingLinks(ShareTarget.PROFILE, provider.id, true, [
        {
          id: 'sms',
          parameters: [
            {
              key: 'utm_medium',
              value: 'sms',
            },
            {
              key: 'utm_source',
              value: 'client_invite_from_pro',
            },
            {
              key: 'utm_content',
              value: useAlternativeCopy ? 'alternativeCopy' : 'default',
            },
          ],
        },
      ]);

      const body = useAlternativeCopy
        ? `I'm making moves! Start booking me on StyleSeat. Create an account to check my availability and to book me! ${links.sms}`
        : `Book your next appointment with me on StyleSeat! It's super easy, plus you can see a calendar view of all my available times. ${links.sms}`;
      const url = generateSmsUrl(phone.number, body);
      if (onInviteClick) {
        onInviteClick(contact.id);
      }
      openExternalUrl(url, null);
    } else {
      analytics.track('pro_contact_details_add_info_clicked', trackingParams);
      handleContactClick();
    }
  };

  const cellPhone = getCellPhone(contact.phones);
  const { id } = contact;
  const testID = isRecent ? `contact-list-recent-${id}` : `contact-list-all-${id}`;

  return (
    <Animated.View
      style={{
        backgroundColor: colorInterpolation,
      }}
    >
      <Box
        borderWidth={1}
        borderColor={colors.lightGrey}
        borderXWidth={0}
        color={selected ? colors.chill : colors.whiteWash}
        testID={`${testID}${selected ? '-selected' : ''}`}
      >
        <Box
          style={{
            opacity: disabled ? 0.4 : null,
            flex: 1,
          }}
          paddingLeft={16}
          paddingRight={16}
          paddingY={16}
        >
          <Inline
            justifyContent="space-between"
            alignItems="center"
            maxWidth="100%"
          >
            <TouchableWithoutFeedback onPress={handleContactClick} style={{ width: '100%' }}>
              <View
                style={{
                  maxWidth: '100%',
                  flex: 1,
                }}
              >
                <Inline space={12} alignItems="center" flex={1}>
                  <View style={{ width: 35 }}>
                    <AvatarIcon
                      icon={contact.thumbnail}
                      name={contact.name}
                    />
                    {contact.blocked ? (
                      <GlyphIcon
                        content={BLOCKED}
                        styleName="blocked"
                        testID="blocked"
                      />
                    ) : null}
                  </View>
                  <Box
                    style={{
                      width: 'calc(100% - 59px)',
                    }}
                  >
                    <BodyText
                      testID="contact-details-name"
                      numberOfLines={1}
                    >
                      {contact.name}
                    </BodyText>
                    {isSearchingByPhoneNumber ? contact.phones.map(phone => {
                      const phoneType = getPhoneType(phone);

                      return (matchWithSavedPhoneNumber(phone.number, searchTerm) ? (
                        <BodyText
                          appearance="small"
                          testID={`${phoneType.toLowerCase()}-phone`}
                          key={`${phoneType.toLowerCase()}-phone-${phone.number}`}
                        >
                          {`${phoneType}: ${formatPhoneNumber(phone.number)}`}
                        </BodyText>
                      ) : null);
                    }) : null}
                  </Box>
                </Inline>
              </View>
            </TouchableWithoutFeedback>
            {disabled ? (
              <GlyphIcon
                content={INFO}
                style={{ fontSize: '24px' }}
                onPress={handleInfoClick}
                testID="info"
              />
            ) : null}
            {!contact.blocked && !disabled && showInviteActions ? (
              <Button
                onClick={handleActionClick}
                size="small"
                testID={cellPhone ? 'invite-to-book' : 'add-info'}
                style={{ height: 'auto' }}
                appearance={
                  (!cellPhone && 'link-lowercase')
                  || (hasBeenInvitedBefore && 'text-light')
                  || 'primary-neue'
                }
              >
                {(!cellPhone && 'Add Info') || (hasBeenInvitedBefore && 'Invite to Book') || 'Invite to StyleSeat'}
              </Button>
            ) : null}
          </Inline>
        </Box>
      </Box>
    </Animated.View>
  );
}

ContactListItem.propTypes = {
  contact: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  disabledContactInfo: PropTypes.func,
  isSearchingByPhoneNumber: PropTypes.bool,
  provider: PropTypes.object,
  searchTerm: PropTypes.string,
  selectContact: PropTypes.func,
  selected: PropTypes.bool,
  shouldAnimate: PropTypes.bool,
  isRecent: PropTypes.bool,
  animationDelay: PropTypes.number,
  showInviteActions: PropTypes.bool,
  trackingParams: PropTypes.object,
  hasBeenInvitedBefore: PropTypes.bool,
  onInviteClick: PropTypes.func,
};

ContactListItem.defaultProps = {
  disabled: false,
  disabledContactInfo: () => { },
  isSearchingByPhoneNumber: false,
  provider: undefined,
  searchTerm: undefined,
  selectContact: () => { },
  selected: false,
  showInviteActions: false,
  shouldAnimate: false,
  isRecent: false,
  animationDelay: undefined,
  trackingParams: undefined,
  hasBeenInvitedBefore: false,
  onInviteClick: undefined,
};
